/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit,
	ViewChild
} from '@angular/core';
import {
	AbstractControl,
	UntypedFormControl
} from '@angular/forms';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	PaymentAllocationsComponent
} from '@insurance/components/payment-allocations/payment-allocations.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceHelper
} from '@insurance/helpers/insurance.helper';
import {
	IInsurancePaymentSummary
} from '@insurance/interfaces/insurance-payment-summary.interface';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

@Component({
	selector: 'adjustment-write-off-amount',
	templateUrl: './adjustment-write-off-amount.component.html',
	styleUrls: [
		'./adjustment-write-off-amount.component.scss'
	]
})

/**
 * A component representing a wizard step to display the payment details and
 * collect an amount for a policy term write off.
 *
 * @export
 * @class AdjustmentWriteOffAmountComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class AdjustmentWriteOffAmountComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the adjustment write off component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used to lookup insurance modules.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public insuranceService: InsuranceService,
		public entityInstanceApiService: EntityInstanceApiService,
		public siteLayoutService: SiteLayoutService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the payment allocations child component reference.
	 *
	 * @type {PaymentAllocationsComponent}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	@ViewChild('paymentAllocations')
	public paymentAllocations: PaymentAllocationsComponent;

	/**
	 * Gets or sets the transaction associated with the payment.
	 *
	 * @type {IEntityInstance}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public transaction: IEntityInstance;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the PolicyTerm entity instance associated to the current
	 * data.
	 *
	 * @type {IEntityInstance}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public policyTermEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the ledger entity instance associated to the current
	 * policy term.
	 *
	 * @type {IEntityInstance}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public ledgerEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the payment allocation settings retrieved from
	 * the product associated to the transaction.
	 *
	 * @type {any}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public paymentAllocationSettings: any;

	/**
	 * Gets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get currentData(): any
	{
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets a value indicating whether payment allocations are enabled.
	 *
	 * @type {boolean}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get paymentAllocationsEnabled(): boolean
	{
		return this.paymentAllocationSettings?.enabled ?? false;
	}

	/**
	 * Gets the amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get amountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'amount');
	}

	/**
	 * Gets the new balance field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get newBalanceFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'newBalance');
	}

	/**
	 * Gets the transaction field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get transactionFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'transactionId');
	}

	/**
	 * Gets the allocation amount field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public get allocationAmountFieldConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout
			?.find(field => field.key === 'allocationAmount');
	}

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public ngOnInit(): void
	{
		this.initializeComponent();
	}

	/**
	 * Initializes the component asynchronously.
	 *
	 * @async
	 * @return {Promise<void>}
	 * The promise indicating the completion of the component initialization.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public async initializeComponent(): Promise<void>
	{
		const policyTermId: number = this.currentData.id;

		this.policyTermEntityInstance =
			await this.insuranceService.getPolicyTerm(
				policyTermId);

		this.ledgerEntityInstance =
			await this.insuranceService.getLedger(
				policyTermId);

		const ledgerTransactionInstances: IEntityInstance[] =
			await this.insuranceService.getLedgerTransactions(
				this.ledgerEntityInstance.id);

		const priorTermTransfer: number =
			InsuranceHelper.getPriorTermTransferBalance(
				ledgerTransactionInstances);

		const policyTermWrittenOffBalance: number =
			InsuranceHelper.getPolicyTermWrittenOffBalance(
				ledgerTransactionInstances);

		const payments: number =
			InsuranceHelper.getPaymentsBalance(
				ledgerTransactionInstances);

		const operationalFees: number =
			InsuranceHelper.getPolicyTermOperationalFeeBalance(
				this.policyTermEntityInstance,
				ledgerTransactionInstances);

		const futureIssuedTransactions: IEntityInstance[] =
			await this.insuranceService.getFutureIssuedTransactions(
				this.policyTermEntityInstance);

		this.paymentAllocationSettings =
			await this.insuranceService.getProductPaymentAllocationSettings(
				this.policyTermEntityInstance.data.productName);

		// If we are past bind, only use the draft payment schedule when
		// future issued transactions exist.
		let accountingPayments: any[] =
			this.policyTermEntityInstance
				?.data.accounting?.paymentSchedule?.payments;

		if (futureIssuedTransactions.length > 0
			&& !AnyHelper.isNull(
				accountingPayments))
		{
			const actionResponse: any =
				await this.insuranceService.generateDraftPaymentPlanSchedule(
					this.policyTermEntityInstance.id,
					InsuranceConstants.insuranceEntityTypeGroups.policyTerms);

			accountingPayments =
				actionResponse?.body?.value.payments;
		}

		const remainingPayments: IInsurancePaymentSummary[] =
			await this.insuranceService.getRemainingPaymentSummaries(
				policyTermId,
				InsuranceConstants.insuranceEntityTypeGroups.policyTerms,
				accountingPayments);
		const nextPayment: IInsurancePaymentSummary =
			await this.insuranceService.getNextInstallmentPayment(
				policyTermId,
				InsuranceConstants.insuranceEntityTypeGroups.policyTerms,
				remainingPayments,
				false);

		let accountingData: any;
		let paymentDetailsData: any;
		const minimumDue: number = nextPayment?.due ?? 0;

		if (this.policyTermEntityInstance.data.status ===
			InsuranceConstants.policyTermStatusTypes.active)
		{
			accountingData =
				this.policyTermEntityInstance.data.accounting;

			paymentDetailsData =
				{
					premium: accountingData.writtenPremium,
					fees: accountingData.writtenFees + operationalFees,
					taxes: accountingData.writtenTaxes,
					total: accountingData.writtenTotal + operationalFees,
					priorTermTransfer: priorTermTransfer,
					policyTermWrittenOffBalance: policyTermWrittenOffBalance,
					payments: payments,
					remainingBalance:
						this.ledgerEntityInstance.data.balance,
					minimumDue: minimumDue
				};
		}
		else
		{
			const policyTermTransactionInstance: IEntityInstance =
				await this.insuranceService
					.getLatestPolicyTermTransactionByPolicyTerm(
						this.policyTermEntityInstance.id);

			accountingData =
				policyTermTransactionInstance.data.accounting;

			paymentDetailsData =
				{
					premium: accountingData.directWrittenPremium,
					fees: accountingData.directWrittenFees + operationalFees,
					taxes: accountingData.directWrittenTaxes,
					total: accountingData.directWrittenTotal + operationalFees,
					priorTermTransfer: priorTermTransfer,
					policyTermWrittenOffBalance: policyTermWrittenOffBalance,
					payments: payments,
					remainingBalance:
						accountingData.directWrittenTotal
							+ priorTermTransfer
							+ payments
							+ policyTermWrittenOffBalance
							+ operationalFees,
					minimumDue: minimumDue
				};
		}

		this.context.source.addOrUpdateStepData(
			{
				newBalance: this.currentData.newBalance
					?? paymentDetailsData.remainingBalance,
				paymentDetails: this.currentData.paymentDetailsData
					?? paymentDetailsData,
				amount: this.currentData.amount,
				type: this.currentData.type ??
					InsuranceConstants.accountingTypes.premium,
				paymentAllocationsEnabled: this.paymentAllocationsEnabled
			});

		this.performPostInitActions();

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public validityChanged(
		isValid: boolean): void
	{
		this.context.source.validStepChanged(
			isValid
				&& (!this.paymentAllocationsEnabled
					|| this.paymentAllocations?.valid));
	}

	/**
	 * Handles the validity changed event for the payment allocations component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the payment allocations component.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public paymentAllocationsValidityChanged(
		isValid: boolean): void
	{
		this.context.source.validStepChanged(
			isValid
			&& this.dynamicFormlyLayout[0]
				.formControl
				.parent
				.valid);
	}

	/**
	 * Loads the transactions for the transaction field.
	 *
	 * @async
	 * @return {Promise<IEntityInstance[]>}
	 * The promise containing the list of transaction options.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public loadTransactions(): Promise<IEntityInstance[]>
	{
		return this.insuranceService
			.getPolicyTermTransactionsByPolicyTermId(
				this.currentData.id);
	}

	/**
	 * Handles the transaction field change event.
	 *
	 * @async
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration.
	 * @return {Promise<void>}
	 * The promise indicating the completion of the transaction change handling.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	public async onTransactionChange(
		field: FormlyFieldConfig): Promise<void>
	{
		this.transaction =
			AnyHelper.isNullOrWhitespace(
				field.formControl.value)
				? null
				: await this.insuranceService.getPolicyTermTransaction(
					field.formControl.value,
					InsuranceConstants.policyTermTransactionPrefix
						+ `${this.policyTermEntityInstance.data.productName}`);

		this.transactionFieldConfig
			.formControl
			?.updateValueAndValidity();
	}

	/**
	 * Handles the post initialization action.
	 * This will create the dynamic formly layout for display component creation
	 * and permissions.
	 *
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	private performPostInitActions(): void
	{
		const allocationFieldsExpressions = {
			hide: () =>
				!this.paymentAllocationsEnabled
		};

		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'type',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Write Off Type',
						required: true,
						options:
							<IDropdownOption[]>
							[
								{
									label:
										InsuranceConstants.accountingTypes.premium,
									value:
										InsuranceConstants.accountingTypes.premium,
								},
								{
									label:
										InsuranceConstants.accountingTypes.fee,
									value:
										InsuranceConstants.accountingTypes.fee,
								},
								{
									label:
										InsuranceConstants.accountingTypes.tax,
									value:
										InsuranceConstants.accountingTypes.tax,
								}
							]
					}
				},
				{
					key: 'amount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Write Off Amount',
						useCurrency: true,
						change:
							(field: FormlyFieldConfig,
								_event: any) =>
							{
								const newBalance: number =
									this.currentData
										.paymentDetails.remainingBalance
									- (field.formControl.value || 0);

								this.newBalanceFieldConfig
									.formControl
									.setValue(newBalance);
							}
					},
					validators: {
						positivePaymentAmount: {
							expression: ((
								control: UntypedFormControl,
								field: FormlyFieldConfig) =>
								this.positivePaymentAmountValidator(
									control,
									field)),
							message:
								'A value above zero dollars must be written off.'
						},
						allowedTotalWritten: {
							expression: ((
								control: UntypedFormControl,
								field: FormlyFieldConfig) =>
								this.allowedTotalRemainingBalanceValidator(
									control,
									field)),
							message:
								'A value less than the total remaining balance is'
								+ ' required.'
						}
					}
				},
				{
					key: 'newBalance',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'New Balance',
						disabled: true,
						useCurrency: true
					}
				},
				{
					key: 'transactionId',
					type: FormlyConstants.customControls.customDataSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Transaction',
						placeholder: 'Select an Option',
						required: true,
						showClear: true,
						dataPromise:
							this.loadTransactions.bind(this),
						labelTemplate: '${item.data.transactionNumber}',
						valueTemplate: '${item.id}',
						change: this.onTransactionChange.bind(this)
					},
					expressions: allocationFieldsExpressions,
					validators: {
						transactionAmountMustBeGreaterThanZero: {
							expression: () =>
								AnyHelper.isNull(
									this.transaction)
									|| AnyHelper.isNull(
										this.transaction
											.data
											.accounting
											?.directWrittenTotal)
									|| this.transaction
										.data
										.accounting
										.directWrittenTotal > 0,
							message:
								'Write-off adjustment cannot be applied to a '
									+ 'transaction with a negative '
									+ 'Transaction Amount.'
						}
					}
				},
				{
					key: 'allocationAmount',
					type: FormlyConstants.customControls.customInputNumber,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Allocation Amount',
						useCurrency: true,
						disabled: true
					},
					expressions: allocationFieldsExpressions
				}
			];
	}

	/**
	 * Validates the payment amount is positive
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @param {FormlyFieldConfig} _field
	 * The formly field configuration.
	 * @returns {boolean}
	 * The validation passed or failed.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	private positivePaymentAmountValidator(
		control: AbstractControl,
		_field: FormlyFieldConfig): boolean
	{
		return !AnyHelper.isNullOrWhitespace(control.value)
			&& control.value > 0;
	}

	/**
	 * Validates the payment amount is less than the total remaining balance.
	 *
	 * @param {AbstractControl} control
	 * The form control to get the input value.
	 * @param {FormlyFieldConfig} _field
	 * The formly field configuration.
	 * @returns {boolean}
	 * The validation passed or failed.
	 * @memberof AdjustmentWriteOffAmountComponent
	 */
	private allowedTotalRemainingBalanceValidator(
		control: AbstractControl,
		_field: FormlyFieldConfig): boolean
	{
		const paymentDetails: any =
			this.currentData
				.paymentDetails;

		return control.value <= paymentDetails.remainingBalance;
	}
}